/**
 * O estado de interface de cada nó, calculado pelas regras da tela.
 *
 * Fica separado do `EventRuntimeService` de propósito: o `NodeHost` precisa
 * LER este estado para decidir se desenha o nó, e se ele importasse o serviço
 * teríamos `render → runtime → render`. Um token e uma interface quebram isso —
 * o mesmo motivo pelo qual `EDITOR_CHROME` existe.
 */
import { InjectionToken, Signal } from '@angular/core';
import { EventType } from '../model/behavior';

/** O que as regras dizem sobre um nó, agora. */
export interface NodeUiState {
  readonly visible: boolean;
  readonly disabled: boolean;
  readonly required: boolean;
  /**
   * Opções trocadas em tempo de execução pela ação `setOptions`.
   *
   * Ausente é o normal: a lista vem das props, que são a CONFIGURAÇÃO da tela.
   * Quando presente, ela vence — e mora aqui, e não nas props, porque props são
   * o documento. Alterá-las em tempo de execução faria a tela se editar sozinha,
   * e a próxima gravação levaria a alteração junto.
   */
  readonly options?: readonly { value: string; labelKey: string }[];
}

export const DEFAULT_NODE_STATE: NodeUiState = {
  visible: true,
  disabled: false,
  required: false
};

/** O que o runtime encontrou de errado — vai para a tela "Verificar tela". */
export interface RuntimeIssue {
  readonly kind:
    | 'unknownPath'        // regra aponta para campo que não existe
    | 'unknownTarget'      // ação aponta para nó que não existe
    | 'cascadeTooDeep'     // A muda B, B muda A
    | 'actionNotSupported' // ação declarada que este runtime ainda não executa
    | 'badRule';
  readonly nodeId: string;
  readonly detail: string;
}

export interface ScreenRuntime {
  /** Estado de todos os nós. Signal: o renderer reage sem `detectChanges`. */
  readonly states: Signal<ReadonlyMap<string, NodeUiState>>;

  /** Estado de um nó. Devolve o padrão quando não há regra alguma. */
  stateOf(nodeId: string): NodeUiState;

  /** Problemas acumulados desde o início. */
  readonly issues: Signal<readonly RuntimeIssue[]>;

  /**
   * Dispara um evento de um nó.
   *
   * É por aqui que os gatilhos que o formulário não produz sozinho chegam ao
   * runtime: a grade chama com `onRowSelect`, o botão com `onClick`. O método
   * existia no serviço desde o início e não estava no contrato — então nenhum
   * renderer conseguia chamá-lo, e os dois gatilhos eram configuráveis na aba
   * Comportamento sem nunca disparar.
   */
  trigger(nodeId: string, event: EventType): void;
}

export const SCREEN_RUNTIME = new InjectionToken<ScreenRuntime>('SCREEN_RUNTIME');
